import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FiPlus, FiSearch, FiStar, FiPlay, FiEdit2, FiDownload, FiMoreVertical,
  FiCopy, FiGlobe, FiLock, FiShare2, FiClock, FiTrash2, FiX, FiFileText,
  FiUsers, FiTruck, FiCalendar, FiMapPin, FiHome, FiShield, FiGrid, FiList,
  FiChevronDown, FiChevronUp, FiChevronLeft, FiChevronRight, FiFilter,
  FiRefreshCw, FiArrowUp, FiArrowDown
} from 'react-icons/fi';
import {
  HiOutlineDocumentReport, HiOutlineTable, HiOutlineChartPie
} from 'react-icons/hi';
import {
  getAccessibleReports,
  getFavoriteReports,
  getReportTemplates,
  toggleFavorite,
  togglePublic,
  deleteReport,
  cloneReport,
  executeReport,
  exportReport,
  downloadExport,
  shareWithUser,
  shareWithRole,
  getReportShares,
  removeShare,
  scheduleReport,
  disableSchedule
} from '../../../api/reports';
import './ReportList.css';

// Data source icons and colors
const dataSourceConfig = {
  USERS: { icon: FiUsers, color: '#3b82f6', bg: '#eff6ff', label: 'Users' },
  VEHICLES: { icon: FiTruck, color: '#f59e0b', bg: '#fffbeb', label: 'Vehicles' },
  BOOKINGS: { icon: FiCalendar, color: '#10b981', bg: '#ecfdf5', label: 'Bookings' },
  CONFERENCE_ROOMS: { icon: FiHome, color: '#8b5cf6', bg: '#f5f3ff', label: 'Rooms' },
  ROUTES: { icon: FiMapPin, color: '#ef4444', bg: '#fef2f2', label: 'Routes' },
  LOCATIONS: { icon: FiMapPin, color: '#06b6d4', bg: '#ecfeff', label: 'Locations' },
  CUSTOM_ROLES: { icon: FiShield, color: '#ec4899', bg: '#fdf2f8', label: 'Roles' },
  AMENITIES: { icon: FiGrid, color: '#14b8a6', bg: '#f0fdfa', label: 'Amenities' }
};

const reportTypeConfig = {
  TABULAR: { icon: HiOutlineTable, label: 'Table' },
  SUMMARY: { icon: HiOutlineChartPie, label: 'Summary' },
  MATRIX: { icon: FiGrid, label: 'Matrix' }
};

const ITEMS_PER_PAGE_OPTIONS = [10, 25, 50, 100];

function ReportList() {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDataSource, setSelectedDataSource] = useState('');
  const [executing, setExecuting] = useState({});
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [viewMode, setViewMode] = useState('list'); // Default to list for scalability

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);

  // Sorting
  const [sortField, setSortField] = useState('reportName');
  const [sortDirection, setSortDirection] = useState('asc');

  const dropdownRef = useRef(null);

  // Modal states
  const [showShareModal, setShowShareModal] = useState(false);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  const [shareType, setShareType] = useState('user');
  const [shareTarget, setShareTarget] = useState('');
  const [sharePermission, setSharePermission] = useState('VIEW');
  const [cronExpression, setCronExpression] = useState('0 0 8 * * ?');
  const [emailRecipients, setEmailRecipients] = useState('');
  const [currentShares, setCurrentShares] = useState([]);
  const [modalLoading, setModalLoading] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    loadReports();
  }, [filter]);

  useEffect(() => {
    setCurrentPage(1); // Reset to first page when filters change
  }, [searchTerm, selectedDataSource, filter]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setActiveDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadReports = async () => {
    setLoading(true);
    setError(null);
    try {
      let data;
      if (filter === 'favorites') {
        data = await getFavoriteReports();
      } else if (filter === 'templates') {
        data = await getReportTemplates();
      } else {
        data = await getAccessibleReports();
      }
      setReports(data);
    } catch (err) {
      setError('Failed to load reports: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  // Filter, sort, and paginate reports
  const processedReports = useMemo(() => {
    let filtered = [...reports];

    // Apply search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(report =>
        report.reportName.toLowerCase().includes(term) ||
        report.description?.toLowerCase().includes(term) ||
        report.dataSource?.toLowerCase().includes(term)
      );
    }

    // Apply data source filter
    if (selectedDataSource) {
      filtered = filtered.filter(report => report.dataSource === selectedDataSource);
    }

    // Apply tab filter
    if (filter === 'my') {
      const userId = localStorage.getItem('userId');
      filtered = filtered.filter(report => report.createdBy === userId);
    } else if (filter === 'public') {
      filtered = filtered.filter(report => report.isPublic);
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];

      // Handle null/undefined values
      if (aVal == null) aVal = '';
      if (bVal == null) bVal = '';

      // Handle different types
      if (typeof aVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = bVal.toLowerCase();
      }

      if (sortDirection === 'asc') {
        return aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
      } else {
        return aVal > bVal ? -1 : aVal < bVal ? 1 : 0;
      }
    });

    return filtered;
  }, [reports, searchTerm, selectedDataSource, filter, sortField, sortDirection]);

  // Paginated reports
  const paginatedReports = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return processedReports.slice(startIndex, startIndex + itemsPerPage);
  }, [processedReports, currentPage, itemsPerPage]);

  const totalPages = Math.ceil(processedReports.length / itemsPerPage);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handleToggleFavorite = async (e, reportId, isFavorite) => {
    e.stopPropagation();
    try {
      await toggleFavorite(reportId, !isFavorite);
      loadReports();
    } catch (err) {
      alert('Failed to update favorite status: ' + err.message);
    }
  };

  const handleTogglePublic = async (reportId, isPublic) => {
    try {
      await togglePublic(reportId, !isPublic);
      setActiveDropdown(null);
      loadReports();
    } catch (err) {
      alert('Failed to update public status: ' + err.message);
    }
  };

  const handleDelete = async (reportId) => {
    if (!window.confirm('Are you sure you want to delete this report?')) return;
    try {
      await deleteReport(reportId);
      setActiveDropdown(null);
      loadReports();
    } catch (err) {
      alert('Failed to delete report: ' + err.message);
    }
  };

  const handleClone = async (reportId, reportName) => {
    const newName = prompt('Enter name for cloned report:', reportName + ' (Copy)');
    if (!newName) return;
    try {
      const cloned = await cloneReport(reportId, newName);
      setActiveDropdown(null);
      navigate(`/admin/reports/builder/${cloned.id}`);
    } catch (err) {
      alert('Failed to clone report: ' + err.message);
    }
  };

  const handleExecute = async (e, reportId) => {
    e.stopPropagation();
    setExecuting(prev => ({ ...prev, [reportId]: true }));
    try {
      const result = await executeReport(reportId, {});
      navigate(`/admin/reports/view/${reportId}`, { state: { result } });
    } catch (err) {
      alert('Failed to execute report: ' + err.message);
    } finally {
      setExecuting(prev => ({ ...prev, [reportId]: false }));
    }
  };

  const handleExport = async (reportId, reportName, format) => {
    try {
      const response = await exportReport(reportId, format, {});
      const extension = format === 'EXCEL' ? 'xlsx' : format.toLowerCase();
      downloadExport(response.data, `${reportName}.${extension}`);
      setActiveDropdown(null);
    } catch (err) {
      alert('Failed to export report: ' + err.message);
    }
  };

  const openShareModal = async (report) => {
    setSelectedReport(report);
    setShareType('user');
    setShareTarget('');
    setSharePermission('VIEW');
    setShowShareModal(true);
    setActiveDropdown(null);

    try {
      const shares = await getReportShares(report.id);
      setCurrentShares(shares.shares || []);
    } catch (err) {
      console.error('Failed to load shares:', err);
      setCurrentShares([]);
    }
  };

  const handleShare = async () => {
    if (!shareTarget.trim()) {
      alert('Please enter a user ID or email');
      return;
    }

    setModalLoading(true);
    try {
      if (shareType === 'user') {
        await shareWithUser(selectedReport.id, shareTarget, sharePermission);
      } else {
        await shareWithRole(selectedReport.id, shareTarget, sharePermission);
      }
      alert('Report shared successfully');
      setShareTarget('');
      const shares = await getReportShares(selectedReport.id);
      setCurrentShares(shares.shares || []);
    } catch (err) {
      alert('Failed to share report: ' + err.message);
    } finally {
      setModalLoading(false);
    }
  };

  const handleRemoveShare = async (shareId) => {
    if (!window.confirm('Remove this share?')) return;
    try {
      await removeShare(selectedReport.id, shareId);
      const shares = await getReportShares(selectedReport.id);
      setCurrentShares(shares.shares || []);
    } catch (err) {
      alert('Failed to remove share: ' + err.message);
    }
  };

  const openScheduleModal = (report) => {
    setSelectedReport(report);
    setCronExpression(report.cronExpression || '0 0 8 * * ?');
    setEmailRecipients(report.emailRecipients || '');
    setShowScheduleModal(true);
    setActiveDropdown(null);
  };

  const handleSchedule = async () => {
    if (!cronExpression.trim()) {
      alert('Please enter a cron expression');
      return;
    }

    setModalLoading(true);
    try {
      await scheduleReport(selectedReport.id, cronExpression, emailRecipients);
      alert('Report scheduled successfully');
      setShowScheduleModal(false);
      loadReports();
    } catch (err) {
      alert('Failed to schedule report: ' + err.message);
    } finally {
      setModalLoading(false);
    }
  };

  const handleDisableSchedule = async () => {
    if (!window.confirm('Disable scheduling for this report?')) return;

    setModalLoading(true);
    try {
      await disableSchedule(selectedReport.id);
      alert('Schedule disabled successfully');
      setShowScheduleModal(false);
      loadReports();
    } catch (err) {
      alert('Failed to disable schedule: ' + err.message);
    } finally {
      setModalLoading(false);
    }
  };

  const toggleDropdown = (e, reportId) => {
    e.stopPropagation();
    setActiveDropdown(activeDropdown === reportId ? null : reportId);
  };

  const getSourceConfig = (source) => {
    return dataSourceConfig[source] || { icon: FiFileText, color: '#64748b', bg: '#f1f5f9', label: source };
  };

  const getTypeConfig = (type) => {
    return reportTypeConfig[type] || { icon: HiOutlineTable, label: type };
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <FiArrowUp className="sort-icon inactive" />;
    return sortDirection === 'asc' ?
      <FiArrowUp className="sort-icon" /> :
      <FiArrowDown className="sort-icon" />;
  };

  if (loading) {
    return (
      <div className="reports-page">
        <div className="reports-loading">
          <div className="loading-spinner"></div>
          <p>Loading reports...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="reports-page">
        <div className="reports-error">
          <FiX size={48} />
          <h3>Error Loading Reports</h3>
          <p>{error}</p>
          <button onClick={loadReports}><FiRefreshCw /> Retry</button>
        </div>
      </div>
    );
  }

  return (
    <div className="reports-page">
      {/* Header Row - Title, Search, and Add Button */}
      <div className="page-header">
        <div className="header-title">
          <HiOutlineDocumentReport size={22} />
          <h1>Report Management</h1>
        </div>
        <div className="header-actions">
          <div className="search-box">
            <FiSearch />
            <input
              type="text"
              placeholder="Search by name, email, or p..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <button className="btn-new-report" onClick={() => navigate('/admin/reports/builder')}>
            <FiPlus /> New Report
          </button>
        </div>
      </div>

      {/* Filter Toolbar */}
      <div className="reports-toolbar">
        <div className="toolbar-left">
          <div className="filter-tabs">
            {[
              { key: 'all', label: 'All', icon: FiGrid },
              { key: 'my', label: 'My Reports', icon: FiFileText },
              { key: 'public', label: 'Public', icon: FiGlobe },
              { key: 'favorites', label: 'Favorites', icon: FiStar },
              { key: 'templates', label: 'Templates', icon: HiOutlineDocumentReport }
            ].map(tab => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.key}
                  className={`filter-tab ${filter === tab.key ? 'active' : ''}`}
                  onClick={() => setFilter(tab.key)}
                >
                  <Icon size={14} />
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </div>

          <select
            className="source-select"
            value={selectedDataSource}
            onChange={(e) => setSelectedDataSource(e.target.value)}
          >
            <option value="">All Sources</option>
            {Object.entries(dataSourceConfig).map(([key, config]) => (
              <option key={key} value={key}>{config.label}</option>
            ))}
          </select>
        </div>

        <div className="toolbar-right">
          <div className="view-toggle">
            <button
              className={viewMode === 'list' ? 'active' : ''}
              onClick={() => setViewMode('list')}
              title="List view"
            >
              <FiList size={16} />
            </button>
            <button
              className={viewMode === 'grid' ? 'active' : ''}
              onClick={() => setViewMode('grid')}
              title="Grid view"
            >
              <FiGrid size={16} />
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      {processedReports.length === 0 ? (
        <div className="reports-empty">
          <HiOutlineDocumentReport size={56} />
          <h3>No reports found</h3>
          <p>{searchTerm || selectedDataSource ? 'Try adjusting your filters' : 'Create your first report to start analyzing data'}</p>
          <button onClick={() => navigate('/admin/reports/builder')}>
            <FiPlus /> Create Report
          </button>
        </div>
      ) : viewMode === 'list' ? (
        <>
          <div className="reports-table-container">
            <table className="reports-table">
              <thead>
                <tr>
                  <th className="col-fav"></th>
                  <th className="col-name sortable" onClick={() => handleSort('reportName')}>
                    Report Name <SortIcon field="reportName" />
                  </th>
                  <th className="col-source sortable" onClick={() => handleSort('dataSource')}>
                    Data Source <SortIcon field="dataSource" />
                  </th>
                  <th className="col-type">Type</th>
                  <th className="col-date sortable" onClick={() => handleSort('lastExecuted')}>
                    Last Run <SortIcon field="lastExecuted" />
                  </th>
                  <th className="col-actions">Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginatedReports.map(report => {
                  const sourceConfig = getSourceConfig(report.dataSource);
                  const typeConfig = getTypeConfig(report.reportType);
                  const TypeIcon = typeConfig.icon;

                  return (
                    <tr key={report.id} onClick={() => navigate(`/admin/reports/builder/${report.id}`)}>
                      <td className="col-fav">
                        <button
                          className={`btn-fav ${report.isFavorite ? 'active' : ''}`}
                          onClick={(e) => handleToggleFavorite(e, report.id, report.isFavorite)}
                        >
                          <FiStar size={16} />
                        </button>
                      </td>
                      <td className="col-name">
                        <div className="name-cell">
                          <div className="name-text">
                            <span className="report-name">{report.reportName}</span>
                            {report.description && <span className="report-desc">{report.description}</span>}
                          </div>
                          {report.isPublic && <FiGlobe className="public-badge" title="Public" />}
                        </div>
                      </td>
                      <td className="col-source">
                        <span className="source-badge">
                          {sourceConfig.label}
                        </span>
                      </td>
                      <td className="col-type">
                        <span className="type-badge">
                          <TypeIcon size={12} /> {typeConfig.label}
                        </span>
                      </td>
                      <td className="col-date">
                        {report.lastExecuted ? new Date(report.lastExecuted).toLocaleDateString() : '-'}
                      </td>
                      <td className="col-actions" onClick={e => e.stopPropagation()}>
                        <div className="action-buttons">
                          <button
                            className="btn-action-run"
                            onClick={(e) => handleExecute(e, report.id)}
                            disabled={executing[report.id]}
                            title="Run report"
                          >
                            {executing[report.id] ? <span className="spinner-sm"></span> : <><FiPlay size={14} /> Run</>}
                          </button>
                          <button
                            className="btn-action-edit"
                            onClick={() => navigate(`/admin/reports/builder/${report.id}`)}
                            title="Edit report"
                          >
                            <FiEdit2 size={16} />
                          </button>
                          <div className="dropdown-wrap" ref={activeDropdown === report.id ? dropdownRef : null}>
                            <button className="btn-action-more" onClick={(e) => toggleDropdown(e, report.id)}>
                              <FiMoreVertical size={16} />
                            </button>
                            {activeDropdown === report.id && (
                              <div className="dropdown-menu">
                                <button onClick={() => handleExport(report.id, report.reportName, 'PDF')}>
                                  <FiDownload size={13} /> Export PDF
                                </button>
                                <button onClick={() => handleExport(report.id, report.reportName, 'EXCEL')}>
                                  <FiDownload size={13} /> Export Excel
                                </button>
                                <button onClick={() => handleExport(report.id, report.reportName, 'CSV')}>
                                  <FiDownload size={13} /> Export CSV
                                </button>
                                <div className="dropdown-divider" />
                                <button onClick={() => handleClone(report.id, report.reportName)}>
                                  <FiCopy size={13} /> Clone
                                </button>
                                <button onClick={() => handleTogglePublic(report.id, report.isPublic)}>
                                  {report.isPublic ? <><FiLock size={13} /> Make Private</> : <><FiGlobe size={13} /> Make Public</>}
                                </button>
                                <button onClick={() => openShareModal(report)}>
                                  <FiShare2 size={13} /> Share
                                </button>
                                <button onClick={() => openScheduleModal(report)}>
                                  <FiClock size={13} /> Schedule
                                </button>
                                <div className="dropdown-divider" />
                                <button className="danger" onClick={() => handleDelete(report.id)}>
                                  <FiTrash2 size={13} /> Delete
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="pagination">
            <div className="pagination-info">
              Showing {((currentPage - 1) * itemsPerPage) + 1} - {Math.min(currentPage * itemsPerPage, processedReports.length)} of {processedReports.length}
            </div>
            <div className="pagination-controls">
              <select
                value={itemsPerPage}
                onChange={(e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }}
                className="page-size-select"
              >
                {ITEMS_PER_PAGE_OPTIONS.map(size => (
                  <option key={size} value={size}>{size} / page</option>
                ))}
              </select>
              <div className="page-nav">
                <button
                  onClick={() => setCurrentPage(1)}
                  disabled={currentPage === 1}
                  title="First page"
                >
                  <FiChevronLeft /><FiChevronLeft />
                </button>
                <button
                  onClick={() => setCurrentPage(p => p - 1)}
                  disabled={currentPage === 1}
                  title="Previous page"
                >
                  <FiChevronLeft />
                </button>
                <span className="page-indicator">
                  Page {currentPage} of {totalPages || 1}
                </span>
                <button
                  onClick={() => setCurrentPage(p => p + 1)}
                  disabled={currentPage >= totalPages}
                  title="Next page"
                >
                  <FiChevronRight />
                </button>
                <button
                  onClick={() => setCurrentPage(totalPages)}
                  disabled={currentPage >= totalPages}
                  title="Last page"
                >
                  <FiChevronRight /><FiChevronRight />
                </button>
              </div>
            </div>
          </div>
        </>
      ) : (
        <>
          <div className="reports-grid">
            {paginatedReports.map(report => {
              const sourceConfig = getSourceConfig(report.dataSource);
              const typeConfig = getTypeConfig(report.reportType);
              const SourceIcon = sourceConfig.icon;
              const TypeIcon = typeConfig.icon;

              return (
                <div
                  key={report.id}
                  className="report-card-compact"
                  onClick={() => navigate(`/admin/reports/builder/${report.id}`)}
                >
                  <div className="card-header-compact">
                    <div className="card-icon-sm" style={{ background: sourceConfig.bg, color: sourceConfig.color }}>
                      <SourceIcon size={16} />
                    </div>
                    <button
                      className={`btn-fav-sm ${report.isFavorite ? 'active' : ''}`}
                      onClick={(e) => handleToggleFavorite(e, report.id, report.isFavorite)}
                    >
                      <FiStar size={14} />
                    </button>
                  </div>
                  <div className="card-body-compact">
                    <h4>{report.reportName}</h4>
                    <p>{report.description || 'No description'}</p>
                  </div>
                  <div className="card-footer-compact">
                    <span className="tag-sm" style={{ background: sourceConfig.bg, color: sourceConfig.color }}>
                      {sourceConfig.label}
                    </span>
                    <span className="tag-sm gray">
                      <TypeIcon size={10} /> {typeConfig.label}
                    </span>
                    {report.isPublic && <FiGlobe className="public-icon-sm" />}
                  </div>
                  <div className="card-actions-compact" onClick={e => e.stopPropagation()}>
                    <button
                      className="btn-icon-run"
                      onClick={(e) => handleExecute(e, report.id)}
                      disabled={executing[report.id]}
                      title="Run report"
                    >
                      {executing[report.id] ? <span className="spinner-sm"></span> : <FiPlay size={16} />}
                    </button>
                    <button className="btn-icon-more" onClick={(e) => toggleDropdown(e, report.id)} title="More options">
                      <FiMoreVertical size={16} />
                    </button>
                    {activeDropdown === report.id && (
                      <div className="dropdown-menu dropdown-menu-card" ref={dropdownRef}>
                        <button onClick={() => navigate(`/admin/reports/builder/${report.id}`)}>
                          <FiEdit2 size={13} /> Edit
                        </button>
                        <button onClick={() => handleExport(report.id, report.reportName, 'EXCEL')}>
                          <FiDownload size={13} /> Export
                        </button>
                        <button onClick={() => handleClone(report.id, report.reportName)}>
                          <FiCopy size={13} /> Clone
                        </button>
                        <button onClick={() => openShareModal(report)}>
                          <FiShare2 size={13} /> Share
                        </button>
                        <div className="dropdown-divider" />
                        <button className="danger" onClick={() => handleDelete(report.id)}>
                          <FiTrash2 size={13} /> Delete
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Pagination for Grid */}
          <div className="pagination">
            <div className="pagination-info">
              Showing {((currentPage - 1) * itemsPerPage) + 1} - {Math.min(currentPage * itemsPerPage, processedReports.length)} of {processedReports.length}
            </div>
            <div className="pagination-controls">
              <select
                value={itemsPerPage}
                onChange={(e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }}
                className="page-size-select"
              >
                {ITEMS_PER_PAGE_OPTIONS.map(size => (
                  <option key={size} value={size}>{size} / page</option>
                ))}
              </select>
              <div className="page-nav">
                <button onClick={() => setCurrentPage(1)} disabled={currentPage === 1}>
                  <FiChevronLeft /><FiChevronLeft />
                </button>
                <button onClick={() => setCurrentPage(p => p - 1)} disabled={currentPage === 1}>
                  <FiChevronLeft />
                </button>
                <span className="page-indicator">Page {currentPage} of {totalPages || 1}</span>
                <button onClick={() => setCurrentPage(p => p + 1)} disabled={currentPage >= totalPages}>
                  <FiChevronRight />
                </button>
                <button onClick={() => setCurrentPage(totalPages)} disabled={currentPage >= totalPages}>
                  <FiChevronRight /><FiChevronRight />
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Share Modal */}
      {showShareModal && selectedReport && (
        <div className="modal-overlay" onClick={() => setShowShareModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2><FiShare2 /> Share Report</h2>
              <button className="btn-close" onClick={() => setShowShareModal(false)}><FiX /></button>
            </div>
            <div className="modal-body">
              <p className="modal-subtitle">Share "{selectedReport.reportName}" with others</p>

              <div className="form-row">
                <div className="form-group">
                  <label>Share with</label>
                  <select value={shareType} onChange={e => setShareType(e.target.value)}>
                    <option value="user">User</option>
                    <option value="role">Role</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Permission</label>
                  <select value={sharePermission} onChange={e => setSharePermission(e.target.value)}>
                    <option value="VIEW">View Only</option>
                    <option value="EDIT">Can Edit</option>
                    <option value="ADMIN">Full Access</option>
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label>{shareType === 'user' ? 'User ID or Email' : 'Role ID'}</label>
                <input
                  type="text"
                  value={shareTarget}
                  onChange={e => setShareTarget(e.target.value)}
                  placeholder={shareType === 'user' ? 'Enter user ID or email...' : 'Enter role ID...'}
                />
              </div>

              <button className="btn-primary full-width" onClick={handleShare} disabled={modalLoading}>
                {modalLoading ? 'Sharing...' : 'Share Report'}
              </button>

              {currentShares.length > 0 && (
                <div className="shares-list">
                  <h4>Current Shares</h4>
                  {currentShares.map(share => (
                    <div key={share.id} className="share-item">
                      <div>
                        <span className="share-type">{share.shareType}</span>
                        <span className="share-target">{share.targetId || share.targetName}</span>
                        <span className="share-permission">{share.permission}</span>
                      </div>
                      <button onClick={() => handleRemoveShare(share.id)}>Remove</button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Schedule Modal */}
      {showScheduleModal && selectedReport && (
        <div className="modal-overlay" onClick={() => setShowScheduleModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2><FiClock /> Schedule Report</h2>
              <button className="btn-close" onClick={() => setShowScheduleModal(false)}><FiX /></button>
            </div>
            <div className="modal-body">
              <p className="modal-subtitle">Set up automatic execution for "{selectedReport.reportName}"</p>

              <div className="form-group">
                <label>Schedule (Cron Expression)</label>
                <input
                  type="text"
                  value={cronExpression}
                  onChange={e => setCronExpression(e.target.value)}
                  placeholder="0 0 8 * * ?"
                />
                <span className="form-hint">Format: seconds minutes hours day month weekday</span>
              </div>

              <div className="form-group">
                <label>Quick Presets</label>
                <div className="preset-buttons">
                  <button type="button" onClick={() => setCronExpression('0 0 8 * * ?')}>Daily 8 AM</button>
                  <button type="button" onClick={() => setCronExpression('0 0 8 * * MON')}>Weekly Mon</button>
                  <button type="button" onClick={() => setCronExpression('0 0 8 1 * ?')}>Monthly 1st</button>
                </div>
              </div>

              <div className="form-group">
                <label>Email Recipients</label>
                <input
                  type="text"
                  value={emailRecipients}
                  onChange={e => setEmailRecipients(e.target.value)}
                  placeholder="user@example.com, admin@example.com"
                />
                <span className="form-hint">Comma-separated list of email addresses</span>
              </div>

              <div className="modal-actions">
                <button className="btn-primary" onClick={handleSchedule} disabled={modalLoading}>
                  {modalLoading ? 'Saving...' : 'Save Schedule'}
                </button>
                {selectedReport.isScheduled && (
                  <button className="btn-danger" onClick={handleDisableSchedule} disabled={modalLoading}>
                    Disable Schedule
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ReportList;
